import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core'
import axios from 'axios'

import { getGraphQLEndpoint } from './index.js'

export type GraphQLAuth =
  | { kind: 'none' }
  | { kind: 'bearer'; token: string }

type TypedDocumentString<TResult, TVariables> = DocumentTypeDecoration<
  TResult,
  TVariables
> & {
  toString(): string
}

/** Paging metadata the API attaches to any response with a paged field. */
export type GraphQLPageInfo = {
  readonly has_next_page?: boolean
  readonly total_count?: number
}

export type GraphQLExtensions = {
  readonly page_info?: GraphQLPageInfo
}

type GraphQLResponse<TResult> = {
  data?: TResult
  errors?: Array<{ message: string }>
  extensions?: GraphQLExtensions
}

/** The operation name from a query document, for log context. */
const operationNameOf = (document: string): string =>
  /(?:query|mutation|subscription)\s+(\w+)/.exec(document)?.[1] ?? 'anonymous'

/** Post a document and return the whole response body. Throws on GraphQL errors. */
const postGraphQL = async <TResult, TVariables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  { auth }: { auth: GraphQLAuth }
): Promise<GraphQLResponse<TResult>> => {
  const endpoint = await getGraphQLEndpoint()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }

  if (auth.kind === 'bearer') {
    headers.Authorization = `Bearer ${auth.token}`
  }

  const response = await axios.post<GraphQLResponse<TResult>>(
    endpoint,
    JSON.stringify({
      query: document.toString(),
      variables
    }),
    {
      headers,
      timeout: 30000
    }
  )

  const errors = response.data.errors
  if (errors && errors.length > 0) {
    // A GraphQL failure returns HTTP 200, so axios resolves and nothing
    // reaches the console on its own. React Query then parks the throw in
    // `error` state, where a silent empty table looks the same as a real one.
    console.error(
      `GraphQL ${operationNameOf(document.toString())} failed:`,
      errors.map((error) => error.message)
    )
    throw new Error(errors[0]?.message ?? 'Unknown GraphQL error')
  }

  return response.data
}

/**
 * Execute a typed GraphQL document against the current endpoint.
 *
 * Auth stays explicit at the call site so login flows never send a bearer token
 * by accident. The `as TResult` return is a trust assertion on the response
 * shape — codegen types the document, it does not validate the payload.
 */
export const executeGraphQL = async <TResult, TVariables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  options: { auth: GraphQLAuth }
): Promise<TResult> => {
  const response = await postGraphQL(document, variables, options)
  return response.data as TResult
}

/**
 * Execute a typed GraphQL document and also return the response `extensions`.
 *
 * Use this for a paged field when the caller needs `page_info`. Same auth and
 * trust rules as {@link executeGraphQL}.
 */
export const executeGraphQLWithExtensions = async <TResult, TVariables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  options: { auth: GraphQLAuth }
): Promise<{ data: TResult; extensions: GraphQLExtensions }> => {
  const response = await postGraphQL(document, variables, options)
  return {
    data: response.data as TResult,
    extensions: response.extensions ?? {}
  }
}
